import apiClient from '@shared/services/apiClient';
import type {
  TaskRequestListItem,
  TaskRequest,
  TaskRequestComment,
  TaskRequestCount,
  UserSearchResult,
  CreateTaskRequestPayload,
  UpdateTaskRequestPayload,
  CompleteTaskRequestPayload,
  RejectTaskRequestPayload,
  AssignTaskRequestPayload,
  UpdateChecklistStepPayload,
  TaskRequestFilters,
  TaskStatus,
} from '../types/taskRequest';
import type { PaginatedResult } from '@shared/types/api';

// Helper: unwrap the { data: T } envelope the API returns
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrap<T>(responseData: any): T {
  if (responseData && typeof responseData === 'object' && 'data' in responseData) {
    return responseData.data;
  }
  return responseData;
}

export const taskRequestApi = {
  getTaskRequests: async (
    filters: TaskRequestFilters
  ): Promise<PaginatedResult<TaskRequestListItem>> => {
    const params = new URLSearchParams({
      page: String(filters.page),
      pageSize: String(filters.pageSize),
    });
    if (filters.status) params.append('status', filters.status);
    if (filters.taskType) params.append('taskType', filters.taskType);
    if (filters.priority !== undefined) params.append('priority', String(filters.priority));
    if (filters.assignedToUserId !== undefined)
      params.append('assignedToUserId', String(filters.assignedToUserId));
    if (filters.search) params.append('search', filters.search);
    if (filters.sortColumn) params.append('sortColumn', filters.sortColumn);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    const response = await apiClient.get(`/task-requests?${params}`);
    // API returns { data: [...], meta: { totalCount, page, pageSize, totalPages } }
    const envelope = response.data;
    const totalCount = envelope.meta?.totalCount ?? 0;
    const pageNumber = envelope.meta?.page ?? filters.page;
    const pageSize = envelope.meta?.pageSize ?? filters.pageSize;
    const totalPages = envelope.meta?.totalPages ?? 0;
    return {
      items: envelope.data ?? [],
      totalCount,
      pageNumber,
      pageSize,
      totalPages,
      hasNextPage: pageNumber < totalPages,
      hasPreviousPage: pageNumber > 1,
    };
  },

  getTaskRequestCounts: async (): Promise<TaskRequestCount[]> => {
    const response = await apiClient.get('/task-requests/counts');
    return unwrap<TaskRequestCount[]>(response.data);
  },

  getTaskRequest: async (id: number): Promise<TaskRequest> => {
    const response = await apiClient.get(`/task-requests/${id}`);
    return unwrap<TaskRequest>(response.data);
  },

  createTaskRequest: async (
    payload: CreateTaskRequestPayload
  ): Promise<TaskRequest> => {
    const response = await apiClient.post('/task-requests', payload);
    return unwrap<TaskRequest>(response.data);
  },

  updateTaskRequest: async (
    id: number,
    payload: UpdateTaskRequestPayload
  ): Promise<TaskRequest> => {
    const response = await apiClient.put(`/task-requests/${id}`, payload);
    return unwrap<TaskRequest>(response.data);
  },

  updateTaskRequestStatus: async (
    id: number,
    status: TaskStatus
  ): Promise<TaskRequest> => {
    const response = await apiClient.patch(`/task-requests/${id}/status`, { status });
    return unwrap<TaskRequest>(response.data);
  },

  assignTaskRequest: async (
    id: number,
    payload: AssignTaskRequestPayload
  ): Promise<TaskRequest> => {
    const response = await apiClient.patch(`/task-requests/${id}/assign`, payload);
    return unwrap<TaskRequest>(response.data);
  },

  completeTaskRequest: async (
    id: number,
    payload: CompleteTaskRequestPayload
  ): Promise<TaskRequest> => {
    const response = await apiClient.post(`/task-requests/${id}/complete`, payload);
    return unwrap<TaskRequest>(response.data);
  },

  rejectTaskRequest: async (
    id: number,
    payload: RejectTaskRequestPayload
  ): Promise<TaskRequest> => {
    const response = await apiClient.post(`/task-requests/${id}/reject`, payload);
    return unwrap<TaskRequest>(response.data);
  },

  getTaskRequestComments: async (id: number): Promise<TaskRequestComment[]> => {
    const response = await apiClient.get(`/task-requests/${id}/comments`);
    return unwrap<TaskRequestComment[]>(response.data);
  },

  addTaskRequestComment: async (
    id: number,
    commentText: string,
    createdByUserId?: number,
    createdByUserName?: string
  ): Promise<TaskRequestComment> => {
    const response = await apiClient.post(`/task-requests/${id}/comments`, {
      commentText,
      createdByUserId,
      createdByUserName,
    });
    return unwrap<TaskRequestComment>(response.data);
  },

  updateChecklistStep: async (
    id: number,
    payload: UpdateChecklistStepPayload
  ): Promise<TaskRequest> => {
    const response = await apiClient.patch(`/task-requests/${id}/checklist`, payload);
    return unwrap<TaskRequest>(response.data);
  },

  searchUsers: async (
    query: string,
    limit: number = 10
  ): Promise<UserSearchResult[]> => {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    const response = await apiClient.get(`/users/search?${params}`);
    return unwrap<UserSearchResult[]>(response.data);
  },
};
